import { Phone, Mail, MapPin, Clock, Navigation, Star, Facebook, Instagram, Youtube, Linkedin, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { BUSINESS, VERIFIED_SOCIALS, GOOGLE_REVIEW } from "@/data/business";
import { CITIES } from "@/data/cities";
import { LOCAL_SERVICES } from "@/data/services";

const ICONS = { Facebook, Instagram, Youtube, Linkedin, Twitter };

function SocialIcon({ icon }) {
  if (icon === "TikTok") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M16.6 5.82a4.28 4.28 0 0 1-1-2.82h-3.1v12.4a2.6 2.6 0 1 1-1.86-2.49V9.7a5.7 5.7 0 1 0 4.96 5.65V9.01a7.3 7.3 0 0 0 4.28 1.38V7.28a4.28 4.28 0 0 1-3.28-1.46Z" />
      </svg>
    );
  }
  const Ic = ICONS[icon];
  return Ic ? <Ic className="h-4 w-4" /> : null;
}

export default function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand + NAP */}
          <div>
            <Link to="/" className="inline-flex mb-4">
              <span className="bg-white rounded-sm px-3 py-2 inline-flex items-center">
                <img src="/logo.png" alt="Prosper Garage Door Repair" width="185" height="40" className="h-10 w-auto" />
              </span>
            </Link>
            <p className="text-sm leading-relaxed">
              Your local, licensed and insured garage door repair experts serving
              Prosper, TX and all of Collin County — 24/7.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {VERIFIED_SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  title={s.name}
                  className="grid place-items-center h-9 w-9 rounded-sm bg-white/5 border border-white/10 text-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                  data-testid={`social-${s.name.toLowerCase()}`}
                >
                  <SocialIcon icon={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {LOCAL_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}/prosper`} className="hover:text-red-500 transition-colors">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">Service Areas</h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {CITIES.filter((c) => !c.isCounty).slice(0, 12).map((c) => (
                <li key={c.slug}>
                  <Link to={`/garage-door-repair/${c.slug}`} className="hover:text-red-500 transition-colors" data-testid={`footer-area-${c.slug}`}>
                    {c.name}, TX
                  </Link>
                </li>
              ))}
            </ul>
            <a href="#areas-served" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-red-500 transition-colors" data-testid="footer-all-areas">
              View all areas we serve →
            </a>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">Get In Touch</h4>
            <ul className="space-y-3.5 text-sm">
              <li>
                <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-3 hover:text-red-500 transition-colors" data-testid="footer-phone">
                  <Phone className="h-4 w-4 text-red-600 shrink-0" />
                  <span className="font-bold text-white">{BUSINESS.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 hover:text-red-500 transition-colors break-all" data-testid="footer-email">
                  <Mail className="h-4 w-4 text-red-600 shrink-0" />
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <span>
                  {BUSINESS.address}
                  <a
                    href={`https://maps.apple.com/?daddr=${encodeURIComponent(BUSINESS.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 flex items-center gap-1.5 font-semibold text-white hover:text-red-500 transition-colors"
                    data-testid="footer-apple-directions"
                  >
                    <Navigation className="h-3.5 w-3.5 text-red-600 shrink-0" />
                    Get Directions (Apple Maps)
                  </a>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-red-600 shrink-0" />
                {BUSINESS.hours}
              </li>
              <li>
                <Link to="/garage-door-tips" className="inline-flex items-center gap-3 hover:text-red-500 transition-colors" data-testid="footer-blog">
                  <span className="font-semibold">Garage Door Tips &amp; Guides →</span>
                </Link>
              </li>
              <li>
                <a
                  href={GOOGLE_REVIEW.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3367d6] text-white font-bold text-sm px-4 py-2.5 rounded-sm transition-colors"
                  data-testid="footer-google-review"
                >
                  <Star className="h-4 w-4 fill-white" /> Review us on Google
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Areas We Serve — full service × city link map for navigation + crawlability */}
        <div className="mt-14 pt-10 border-t border-white/10 scroll-mt-24" id="areas-served">
          <h3 className="font-heading font-bold text-white text-lg mb-1">Areas We Serve</h3>
          <p className="text-sm text-slate-400 mb-7">Garage door repair &amp; installation across every Collin County community we cover.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8">
            {CITIES.map((c) => (
              <div key={c.slug} data-testid={`mega-footer-city-${c.slug}`}>
                <Link
                  to={`/garage-door-repair/${c.slug}`}
                  className="font-heading font-bold text-white text-sm hover:text-red-500 transition-colors"
                  data-testid={`mega-city-hub-${c.slug}`}
                >
                  {c.name}, TX
                </Link>
                <ul className="mt-2.5 space-y-1.5 text-[13px]">
                  {LOCAL_SERVICES.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/${s.slug}/${c.slug}`}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`mega-link-${s.slug}-${c.slug}`}
                      >
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-red-500 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-red-500 transition-colors" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/admin" rel="nofollow" className="hover:text-red-500 transition-colors" data-testid="footer-admin">Admin</Link>
          </div>
          <a
            href="https://web-techservices.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-red-500 transition-colors font-semibold text-slate-300"
            data-testid="footer-credit"
          >
            Made by Web-Techservices AI
          </a>
        </div>
      </div>
    </footer>
  );
}
